/* =====================================================================
   Zigante Tartufi — Auto-add Cooling Packaging
   Theme: "Sleek" 2.2.0 (FoxTheme / Dawn-derived)   Cart type: drawer
   Model: ONE cooling unit per CART.
     • If the cart contains >= 1 product that requires cooling, exactly
       ONE "Cooling Packaging and Ice" line is present (quantity 1).
     • When the last perishable is removed, the cooling line is removed.
     • Quantity of perishables and number of different perishables make
       no difference — still exactly one cooling.
   ---------------------------------------------------------------------
   How it works (no fetch-patch, no MutationObserver, no polling):
     • Perishable add-to-cart forms carry data-requires-cooling="true".
       We intercept their submit, add the product with a hidden line
       property _cooling_required so the line is identifiable in the cart.
     • reconcile() reads /cart.js and makes the cooling line match the
       rule (add / remove / cap to 1). It is idempotent.
     • reconcile() runs after our own add AND on the theme's native
       'cart:updated' event, so removals/quantity changes are handled.
   ===================================================================== */
(function () {
  'use strict';

  var COOLING_HANDLE   = 'cold-packaging-and-ice';
  var PERISHABLE_PROP  = '_cooling_required';   // marks a perishable line
  var COOLING_PROP     = '_cooling_marker';     // marks the cooling line
  var busy = false;
  var debounceTimer = null;

  function coolingId() { return window.ZT_COOLING_VARIANT_ID || null; }

  function isPerishableForm(form) {
    var el = form.closest('[data-requires-cooling]') ||
             form.querySelector('[data-requires-cooling]');
    return !!el && el.getAttribute('data-requires-cooling') === 'true';
  }

  function setLoading(form, on) {
    var b = form.querySelector('[type="submit"]');
    if (!b) return;
    b.classList.toggle('btn--loading', on);
    if (on) b.setAttribute('aria-disabled', 'true'); else b.removeAttribute('aria-disabled');
  }

  function getCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
  }

  function isCoolingLine(it) {
    var cid = coolingId();
    return (cid && (it.id === cid || it.variant_id === cid)) ||
           it.handle === COOLING_HANDLE ||
           (it.properties && it.properties[COOLING_PROP]);
  }

  // Make the cart match the rule. Returns true if it changed anything.
  function reconcile() {
    if (busy) return Promise.resolve(false);
    busy = true;
    var cid = coolingId();

    return getCart().then(function (cart) {
      var perishableCount = 0;
      var coolingLines = [];

      cart.items.forEach(function (it) {
        if (isCoolingLine(it)) { coolingLines.push(it); return; }
        if (it.properties && it.properties[PERISHABLE_PROP]) perishableCount++;
      });

      var chain = Promise.resolve();
      var changed = false;

      if (perishableCount >= 1) {
        if (coolingLines.length === 0 && cid) {
          changed = true;
          chain = chain.then(function () {
            var props = {}; props[COOLING_PROP] = '1';
            return postJSON('/cart/add.js', { items: [{ id: cid, quantity: 1, properties: props }] });
          });
        } else {
          // keep exactly one cooling line at qty 1; drop extras
          coolingLines.forEach(function (c, idx) {
            var target = idx === 0 ? 1 : 0;
            if (c.quantity !== target) {
              changed = true;
              chain = chain.then(function () {
                return postJSON('/cart/change.js', { id: c.key, quantity: target });
              });
            }
          });
        }
      } else if (coolingLines.length) {
        // no perishables left -> remove cooling
        coolingLines.forEach(function (c) {
          changed = true;
          chain = chain.then(function () {
            return postJSON('/cart/change.js', { id: c.key, quantity: 0 });
          });
        });
      }

      return chain.then(function () { return changed; });
    }).then(function (changed) {
      busy = false;
      return changed;
    }).catch(function (e) {
      busy = false;
      console.error('[cooling-bundle] reconcile failed', e);
      return false;
    });
  }

  function refreshDrawer(open) {
    if (document.querySelector('cart-drawer')) {
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true, detail: { open: !!open }
      }));
    } else if (open) {
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      window.location.assign(root + 'cart');
    }
  }

  // ---- Add a perishable (intercept its form) ----------------------------
  function addPerishable(form) {
    var fd = new FormData(form);
    var id = Number(fd.get('id'));
    var qty = Number(fd.get('quantity') || 1);
    if (!id) throw new Error('No variant id on form');
    var props = {}; props[PERISHABLE_PROP] = '1';
    return postJSON('/cart/add.js', { items: [{ id: id, quantity: qty, properties: props }] })
      .then(function (r) { if (!r.ok) throw new Error('/cart/add.js ' + r.status); return r.json(); });
  }

  document.addEventListener('submit', async function (e) {
    var form = e.target && e.target.closest && e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    if (!isPerishableForm(form)) return;   // non-perishable: let the theme handle it

    e.preventDefault();
    e.stopPropagation();
    setLoading(form, true);
    try {
      await addPerishable(form);
      await reconcile();        // adds the single cooling line if needed
      refreshDrawer(true);      // open the drawer like a normal add
    } catch (err) {
      console.error('[cooling-bundle] add failed, falling back to native add', err);
      form.submit();
    } finally {
      setLoading(form, false);
    }
  }, true);

  // ---- React to native cart changes (removals, qty edits) ---------------
  function scheduleReconcile() {
    if (busy) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      reconcile().then(function (changed) { if (changed) refreshDrawer(false); });
    }, 150);
  }
  // Sleek dispatches 'cart:updated' after it re-renders following any change.
  document.addEventListener('cart:updated', scheduleReconcile);

  // Reconcile once on load so the cart is consistent (e.g. after navigation).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      reconcile().then(function (changed) { if (changed) refreshDrawer(false); });
    });
  } else {
    reconcile().then(function (changed) { if (changed) refreshDrawer(false); });
  }
})();
